import { chain, getKeys, hasOwnProperty, invariant, noop, omit as omit$1, pick as pick$1 } from "@ariakit/utils";
//#region src/index.ts
function getInternal(store, key) {
	const internals = store.__unstableInternals;
	invariant(internals, "Invalid store");
	return internals[key];
}
function hasUpdatedKey(keys, updatedKey) {
	if (!keys) return true;
	for (const currentKey of keys) if (updatedKey instanceof Set) {
		if (updatedKey.has(currentKey)) return true;
	} else if (currentKey === updatedKey) return true;
	return false;
}
function isSameValue(value, other) {
	return value === other || value !== value && other !== other;
}
function getCleanupPrevState(prevState, state, stateBeforeCleanup, updatedKey) {
	let cleanupPrevState;
	for (const key of getKeys(state)) {
		if (isSameValue(state[key], stateBeforeCleanup[key])) continue;
		if (updatedKey !== void 0 && hasUpdatedKey([key], updatedKey)) continue;
		cleanupPrevState ??= { ...prevState };
		cleanupPrevState[key] = state[key];
	}
	return cleanupPrevState;
}
const MAX_REPAIR_PASSES = 100;
function addKeyedListener(map, keys, listener) {
	if (!keys) return;
	for (const key of keys) {
		let listeners = map.get(key);
		if (!listeners) {
			listeners = /* @__PURE__ */ new Set();
			map.set(key, listeners);
		}
		listeners.add(listener);
	}
}
function deleteKeyedListener(map, keys, listener) {
	if (!map) return;
	if (!keys) return;
	for (const key of keys) {
		const listeners = map.get(key);
		if (!listeners) continue;
		listeners.delete(listener);
		if (!listeners.size) map.delete(key);
	}
}
function getFastPathNotifiedListeners(frame) {
	const notifiedListeners = /* @__PURE__ */ new Set();
	const currentListener = frame.currentListener;
	if (!currentListener) return notifiedListeners;
	for (const listener of frame.keyedListeners) {
		notifiedListeners.add(listener);
		if (listener === currentListener) return notifiedListeners;
	}
	notifiedListeners.clear();
	notifiedListeners.add(currentListener);
	return notifiedListeners;
}
function preserveFastPathNotifiedListeners(frame) {
	frame.notifiedListeners ??= getFastPathNotifiedListeners(frame);
}
function hasFastPathPassedListener(frame, listener) {
	if (!frame.currentListener) return false;
	let foundCurrentKeyedListener = false;
	for (const currentListener of frame.keyedListeners) {
		if (currentListener === frame.currentListener) {
			foundCurrentKeyedListener = true;
			continue;
		}
		if (!foundCurrentKeyedListener) continue;
		if (currentListener === listener) return false;
	}
	let foundListener = false;
	for (const currentListener of frame.group.listeners) {
		if (currentListener === frame.currentListener) return foundListener;
		if (currentListener === listener) foundListener = true;
	}
	return false;
}
function preserveFastPathFrames(fastPathFrames, group, listener) {
	for (const frame of fastPathFrames) {
		if (frame.group !== group) continue;
		if (frame.recovering) continue;
		if (listener && !frame.keyedListeners.has(listener)) continue;
		preserveFastPathNotifiedListeners(frame);
		for (const currentListener of frame.group.listeners) {
			if (currentListener === frame.currentListener) break;
			if (!hasFastPathPassedListener(frame, currentListener)) continue;
			frame.notifiedListeners?.add(currentListener);
		}
	}
}
function preserveFastPathPassedListeners(fastPathFrames, group, listener) {
	for (const frame of fastPathFrames) {
		if (frame.group !== group) continue;
		if (frame.recovering) continue;
		if (!hasFastPathPassedListener(frame, listener)) continue;
		preserveFastPathNotifiedListeners(frame);
		frame.notifiedListeners?.add(listener);
	}
}
function preserveFastPathPassedKeyedListeners({ fastPathFrames, group, keys, listener }) {
	const wasRegistered = group.listeners.has(listener);
	for (const frame of fastPathFrames) {
		if (frame.group !== group) continue;
		if (frame.recovering) continue;
		if (!keys.includes(frame.updatedKey)) continue;
		if (hasFastPathPassedListener(frame, listener)) {
			preserveFastPathNotifiedListeners(frame);
			frame.notifiedListeners?.add(listener);
		} else if (wasRegistered) {
			preserveFastPathNotifiedListeners(frame);
			frame.recoverToLive = true;
		}
	}
}
function clearFastPathNotifiedListener(fastPathFrames, group, listener) {
	for (const frame of fastPathFrames) {
		if (frame.group !== group) continue;
		frame.notifiedListeners?.delete(listener);
	}
}
function addFastPathKeyedListener({ fastPathFrames, group, keys, listener }) {
	for (const frame of fastPathFrames) {
		if (frame.group !== group) continue;
		if (frame.recovering) continue;
		if (!keys.includes(frame.updatedKey)) continue;
		frame.keyedListeners.add(listener);
	}
}
function runPendingCleanup(group, listener) {
	if (!group.disposables.size) return;
	const cleanup = group.disposables.get(listener);
	if (!cleanup) return;
	group.disposables.delete(listener);
	cleanup();
}
function setListenerCleanup(group, listener, cleanup) {
	const currentCleanup = group.disposables.get(listener);
	if (!currentCleanup) {
		group.disposables.set(listener, cleanup);
		return;
	}
	group.disposables.set(listener, () => {
		currentCleanup();
		cleanup();
	});
}
function notifyStoreListener(group, listener, state, prevState, getState, updatedKey) {
	if (group.suspendCounts?.has(listener)) return;
	const { disposables } = group;
	const cleanup = disposables.size ? disposables.get(listener) : void 0;
	if (cleanup) {
		disposables.delete(listener);
		const stateBeforeCleanup = state;
		cleanup();
		state = getState?.() ?? state;
		if (state !== stateBeforeCleanup) prevState = getCleanupPrevState(prevState, state, stateBeforeCleanup, updatedKey) ?? prevState;
	}
	const result = listener(state, prevState);
	if (result) setListenerCleanup(group, listener, result);
}
function runLiveListeners({ group, getState, prevState, updatedKey, notifiedListeners }) {
	const allKeysListeners = group.allKeysListeners;
	for (const listener of group.listeners) {
		if (notifiedListeners?.has(listener)) continue;
		if (!allKeysListeners?.has(listener)) {
			if (!hasUpdatedKey(group.listenerKeys.get(listener), updatedKey)) continue;
		}
		notifiedListeners?.add(listener);
		notifyStoreListener(group, listener, getState(), prevState, getState, updatedKey);
	}
}
/**
* Creates a store.
* @param initialState Initial state.
* @param stores Stores to extend.
*/
function createStore(initialState, ...stores) {
	let state = initialState;
	let prevStateBatch = state;
	let destroy = noop;
	let batchPending = false;
	let inDispatch = false;
	let updatedKeys = /* @__PURE__ */ new Set();
	const instances = /* @__PURE__ */ new Set();
	const setups = /* @__PURE__ */ new Set();
	const syncListenerGroup = {
		listeners: /* @__PURE__ */ new Set(),
		disposables: /* @__PURE__ */ new Map(),
		listenerKeys: /* @__PURE__ */ new WeakMap()
	};
	const batchListenerGroup = {
		listeners: /* @__PURE__ */ new Set(),
		disposables: /* @__PURE__ */ new Map(),
		listenerKeys: /* @__PURE__ */ new WeakMap()
	};
	const storeSetup = (callback) => {
		setups.add(callback);
		return () => setups.delete(callback);
	};
	const storeInit = () => {
		const initializedInstances = instances.size;
		const instance = Symbol();
		instances.add(instance);
		const maybeDestroy = () => {
			if (!instances.delete(instance)) return;
			if (instances.size) return;
			destroy();
		};
		if (initializedInstances) return maybeDestroy;
		const stateKeys = getKeys(state);
		const desyncs = [];
		for (const store of stores) {
			const storeState = store?.getState?.();
			if (!storeState) continue;
			const keys = stateKeys.filter((key) => hasOwnProperty(storeState, key));
			if (!keys.length) continue;
			if (stores.length === 1 || keys.length === stateKeys.length) {
				for (const key of keys) desyncs.push(sync(store, [key], (state) => {
					setState(key, state[key], true);
				}));
				continue;
			}
			desyncs.push(subscribe(store, keys, (state, prevState) => {
				for (const key of keys) {
					if (state[key] === prevState[key]) continue;
					setState(key, state[key], true);
				}
			}));
			for (const key of keys) {
				const liveState = store?.getState?.();
				if (!liveState) continue;
				setState(key, liveState[key], true);
			}
		}
		const teardowns = [];
		for (const setup of setups) teardowns.push(setup());
		const cleanups = stores.map(init);
		destroy = chain(...desyncs, ...teardowns, ...cleanups);
		return maybeDestroy;
	};
	const deleteListenerIndexes = (group, listener, keys) => {
		if (keys === void 0) return;
		if (keys) deleteKeyedListener(group.listenersByKey, keys, listener);
		else group.allKeysListeners?.delete(listener);
	};
	const fastPathFrames = [];
	const registerListener = (keys, listener, group = syncListenerGroup) => {
		const listenerKeysValue = keys ? [...keys] : null;
		const wasRegistered = group.listeners.has(listener);
		if (!wasRegistered) clearFastPathNotifiedListener(fastPathFrames, group, listener);
		if (!listenerKeysValue) {
			if (wasRegistered) preserveFastPathFrames(fastPathFrames, group);
			preserveFastPathPassedListeners(fastPathFrames, group, listener);
		} else preserveFastPathPassedKeyedListeners({
			fastPathFrames,
			group,
			keys: listenerKeysValue,
			listener
		});
		if (wasRegistered) {
			preserveFastPathFrames(fastPathFrames, group, listener);
			deleteListenerIndexes(group, listener, group.listenerKeys.get(listener));
		}
		group.listeners.add(listener);
		if (listenerKeysValue) {
			group.listenersByKey ??= /* @__PURE__ */ new Map();
			addKeyedListener(group.listenersByKey, listenerKeysValue, listener);
			addFastPathKeyedListener({
				fastPathFrames,
				group,
				keys: listenerKeysValue,
				listener
			});
		} else {
			group.allKeysListeners ??= /* @__PURE__ */ new Set();
			group.allKeysListeners.add(listener);
		}
		group.listenerKeys.set(listener, listenerKeysValue);
		return () => {
			const cleanup = group.disposables.get(listener);
			group.disposables.delete(listener);
			preserveFastPathFrames(fastPathFrames, group, listener);
			const currentKeys = group.listenerKeys.get(listener);
			deleteListenerIndexes(group, listener, listenerKeysValue);
			if (currentKeys !== listenerKeysValue) deleteListenerIndexes(group, listener, currentKeys);
			group.listenerKeys.delete(listener);
			group.listeners.delete(listener);
			cleanup?.();
		};
	};
	const storeSubscribe = (keys, listener) => registerListener(keys, listener);
	const runInitialListener = (group, listener, prevState) => {
		const shouldSuspend = group.listeners.has(listener);
		if (shouldSuspend) {
			group.suspendCounts ??= /* @__PURE__ */ new Map();
			const count = group.suspendCounts.get(listener) ?? 0;
			group.suspendCounts.set(listener, count + 1);
		}
		let cleanupPrevState;
		try {
			const stateBeforeCleanups = state;
			runPendingCleanup(group, listener);
			if (state !== stateBeforeCleanups) cleanupPrevState = getCleanupPrevState(prevState, state, stateBeforeCleanups);
			const cleanup = listener(state, cleanupPrevState ?? prevState);
			if (cleanup) setListenerCleanup(group, listener, cleanup);
		} finally {
			if (shouldSuspend) {
				const suspendCounts = group.suspendCounts;
				const count = suspendCounts?.get(listener);
				if (count && count > 1) suspendCounts?.set(listener, count - 1);
				else suspendCounts?.delete(listener);
				if (!suspendCounts?.size) delete group.suspendCounts;
			}
		}
	};
	const storeSync = (keys, listener) => {
		runInitialListener(syncListenerGroup, listener, state);
		return registerListener(keys, listener);
	};
	const storeBatch = (keys, listener) => {
		if (!batchListenerGroup.listeners.size && !inDispatch) prevStateBatch = state;
		runInitialListener(batchListenerGroup, listener, prevStateBatch);
		return registerListener(keys, listener, batchListenerGroup);
	};
	const storePick = (keys) => createStore(pick$1(state, keys), finalStore);
	const storeOmit = (keys) => createStore(omit$1(state, keys), finalStore);
	const getState = () => state;
	const runListeners = (group, prevState, updatedKey) => {
		if (!(updatedKey instanceof Set) && !group.allKeysListeners?.size) {
			const keyedListeners = group.listenersByKey?.get(updatedKey);
			if (!keyedListeners) return;
			const frame = {
				group,
				keyedListeners,
				updatedKey,
				currentListener: null
			};
			fastPathFrames.push(frame);
			try {
				for (const listener of keyedListeners) {
					if (frame.notifiedListeners?.has(listener)) continue;
					frame.currentListener = listener;
					frame.notifiedListeners?.add(listener);
					notifyStoreListener(group, listener, state, prevState, getState, updatedKey);
					if (!group.allKeysListeners?.size && !frame.recoverToLive) continue;
					const notifiedListeners = frame.notifiedListeners ?? getFastPathNotifiedListeners(frame);
					frame.notifiedListeners = notifiedListeners;
					frame.recovering = true;
					runLiveListeners({
						group,
						getState,
						prevState,
						updatedKey,
						notifiedListeners
					});
					return;
				}
			} finally {
				fastPathFrames.pop();
			}
			return;
		}
		runLiveListeners({
			group,
			getState,
			prevState,
			updatedKey
		});
	};
	const setState = (key, value, fromStores = false) => {
		if (!hasOwnProperty(state, key)) return;
		const currentValue = state[key];
		const nextValue = typeof value === "function" ? value(currentValue) : value;
		if (isSameValue(nextValue, currentValue)) return;
		const wasInDispatch = inDispatch;
		inDispatch = true;
		const prevState = state;
		const nextState = {
			...state,
			[key]: nextValue
		};
		state = nextState;
		let superseded = false;
		try {
			if (!fromStores && stores.length) {
				for (const store of stores) {
					store?.setState?.(key, nextValue);
					if (isSameValue(state[key], nextValue)) continue;
					superseded = true;
					break;
				}
				if (superseded) {
					let pass = 0;
					for (; pass < MAX_REPAIR_PASSES; pass += 1) {
						let changed = false;
						for (const store of stores) {
							const previousValue = state[key];
							store?.setState?.(key, previousValue);
							if (!isSameValue(state[key], previousValue)) changed = true;
						}
						if (!changed) break;
					}
					if (process.env.NODE_ENV !== "production" && pass === MAX_REPAIR_PASSES) console.warn("Parent stores did not converge after a superseded fan-out; a parent listener may be rewriting this key in a cycle.");
				}
			}
			if (!superseded) runListeners(syncListenerGroup, state === nextState ? prevState : {
				...state,
				[key]: prevState[key]
			}, key);
		} finally {
			inDispatch = wasInDispatch;
		}
		if (!batchListenerGroup.listeners.size) {
			if (!inDispatch) prevStateBatch = state;
			return;
		}
		updatedKeys.add(key);
		if (batchPending) return;
		batchPending = true;
		queueMicrotask(() => {
			batchPending = false;
			const snapshot = state;
			const updatedKeysSnapshot = updatedKeys;
			updatedKeys = /* @__PURE__ */ new Set();
			const prevStateBatchBefore = prevStateBatch;
			runListeners(batchListenerGroup, prevStateBatchBefore, updatedKeysSnapshot);
			if (prevStateBatch === prevStateBatchBefore) prevStateBatch = snapshot;
		});
	};
	const finalStore = {
		getState,
		setState,
		__unstableInternals: {
			setup: storeSetup,
			init: storeInit,
			subscribe: storeSubscribe,
			sync: storeSync,
			batch: storeBatch,
			pick: storePick,
			omit: storeOmit
		}
	};
	return finalStore;
}
/**
* Register a callback function that's called when the store is initialized.
*/
function setup(store, ...args) {
	if (!store) return;
	return getInternal(store, "setup")(...args);
}
/**
* Function that should be called when the store is initialized.
*/
function init(store, ...args) {
	if (!store) return;
	return getInternal(store, "init")(...args);
}
/**
* Registers a listener function that's called after state changes in the store.
*/
function subscribe(store, ...args) {
	if (!store) return;
	return getInternal(store, "subscribe")(...args);
}
/**
* Registers a listener function that's called immediately and synchronously
* whenever the store state changes.
*/
function sync(store, ...args) {
	if (!store) return;
	return getInternal(store, "sync")(...args);
}
/**
* Registers a listener function that's called immediately and after a batch
* of state changes in the store.
*/
function batch(store, ...args) {
	if (!store) return;
	return getInternal(store, "batch")(...args);
}
/**
* Creates a new store with a subset of the current store state and keeps them
* in sync.
*/
function omit(store, ...args) {
	if (!store) return;
	return getInternal(store, "omit")(...args);
}
/**
* Creates a new store with a subset of the current store state and keeps them
* in sync.
*/
function pick(store, ...args) {
	if (!store) return;
	return getInternal(store, "pick")(...args);
}
/**
* Merges multiple stores into a single store.
*/
function mergeStore(...stores) {
	const initialState = {};
	for (const store of stores) {
		const nextState = store?.getState?.();
		if (nextState) Object.assign(initialState, nextState);
	}
	const store = createStore(initialState, ...stores);
	return Object.assign({}, ...stores, store);
}
/**
* Throws when a store prop is passed in conjunction with a default state.
*/
function throwOnConflictingProps(props, store) {
	if (process.env.NODE_ENV === "production") return;
	if (!store) return;
	const defaultKeys = Object.entries(props).filter(([key, value]) => key.startsWith("default") && value !== void 0).map(([key]) => {
		const stateKey = key.replace("default", "");
		return `${stateKey[0]?.toLowerCase() || ""}${stateKey.slice(1)}`;
	});
	if (!defaultKeys.length) return;
	const storeState = store.getState();
	if (!defaultKeys.filter((key) => hasOwnProperty(storeState, key)).length) return;
	throw new Error(`Passing a store prop in conjunction with a default state is not supported.

const store = useSelectStore();
<SelectProvider store={store} defaultValue="Apple" />
                ^             ^

Instead, pass the default state to the topmost store:

const store = useSelectStore({ defaultValue: "Apple" });
<SelectProvider store={store} />

See https://github.com/ariakit/ariakit/pull/2745 for more details.

If there's a particular need for this, please submit a feature request at https://github.com/ariakit/ariakit
`);
}
//#endregion
export { batch, createStore, init, mergeStore, omit, pick, setup, subscribe, sync, throwOnConflictingProps };

//# sourceMappingURL=index.js.map